import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';

/** Viewport width below which the drawer opens as a modal overlay (no squeeze). */
const MODAL_BREAKPOINT = 990;

/** Delay before a hover-opened drawer closes after the pointer leaves both the trigger and the panel. */
const HOVER_CLOSE_DELAY_MS = 3000;

/** Keys that open a focused native select's option list. */
const SELECT_OPEN_KEYS = [' ', 'Enter', 'ArrowUp', 'ArrowDown'];

/**
 * A drawer that opens from the right side.
 *
 * On wide viewports (≥ 990px) the drawer squeezes page content alongside it.
 * The panel is a non-modal dialog (`show()`); we install a focus trap via
 * `trapFocus()` so Tab cycles within the drawer, mirroring the modal-mode
 * a11y contract. Focus moves to the close button on open (skipped for
 * hover-previewed opens, see {@link openOnHover}) and returns to the
 * trigger on close.
 *
 * INTENTIONAL: in this non-modal mode the rest of the page is deliberately NOT
 * marked `inert`/`aria-hidden`. Non-modal drawers are meant to leave the page
 * live — the squeeze menu/filter drawers keep their alongside content usable, and
 * the cart drawer is a hover-preview that fades when the pointer leaves, so the
 * shopper must be able to keep interacting with the page behind it. Forcing modal
 * inerting (or `aria-modal`) here would break that interaction model and
 * misrepresent a still-interactive surface to assistive tech. Full modal semantics
 * are reserved for the narrow-viewport `showModal()` path below.
 *
 * On narrow viewports (< 990px) the drawer overlays with a backdrop. The
 * panel is a modal dialog (`showModal()`) — native focus trap, scroll-lock,
 * and ARIA semantics. Same focus-on-close-button + restore-on-close UX.
 *
 * Dispatches {@link DrawerOpenEvent} and {@link DrawerCloseEvent}.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel - The dialog that owns all visual styles and animation.
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  /** Monotonically increasing counter for z-index stacking of multiple open drawers. */
  static #stackOrder = 0;

  /** @type {boolean} */
  #isClosing = false;

  /** When true, the drawer will reopen after the current close animation finishes. */
  /** @type {{ enter?: 'inline-start', focus?: boolean } | false} */
  #deferredOpen = false;

  /** Element to restore focus to when the drawer closes. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {MediaQueryList} */
  #modalQuery = window.matchMedia(`(max-width: ${MODAL_BREAKPOINT - 1}px)`);

  /** Pending hover-close timer, started when the pointer leaves the trigger/panel. */
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverCloseTimer;

  /**
   * @returns {boolean} Whether the drawer is currently open.
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#modalQuery.addEventListener('change', this.#onModalBreakpointChange);

    // Sync the static stack counter with any --drawer-stack-order set by the
    // synchronous restore script (which runs before this module loads).
    const restoredOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  /**
   * Restore path: the inline script in theme-drawer.liquid set [open] on
   * <theme-drawer> + <dialog> before this module loaded. The dialog is
   * already visible — we just wire close listeners. We deliberately skip
   * trapFocus and focus moves: the user is loading a fresh page and
   * expects focus on main content, not inside a drawer left over from
   * the previous session.
   */
  #onRestore() {
    const { panel } = this.refs;
    if (this.#modalQuery.matches) {
      lockScroll(panel);
    }

    this.#addEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    unlockScroll(this.refs.panel);
    this.#modalQuery.removeEventListener('change', this.#onModalBreakpointChange);
    this.#removeEventListeners();
    removeTrapFocus();
    this.#deferredOpen = false;
    clearTimeout(this.#hoverCloseTimer);
  }

  /**
   * Closes the drawer when the user clicks the backdrop (outside the dialog
   * content area). Only meaningful in modal mode — in sidebar mode the
   * dialog has no backdrop so this handler is inert.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    if (isClickedOutside(event, panel)) {
      if (this.#hasOpenNestedDialog()) return;

      this.close();
    }
  };

  /**
   * @returns {boolean} Whether the drawer panel contains an open dialog other than itself.
   */
  #hasOpenNestedDialog() {
    return this.#getOpenNestedDialog() !== null;
  }

  /**
   * @returns {HTMLDialogElement | null} The open dialog nested inside the drawer panel, if any.
   */
  #getOpenNestedDialog() {
    return /** @type {HTMLDialogElement | null} */ (this.refs.panel.querySelector('dialog[open]'));
  }

  /**
   * Switches the dialog between modal and non-modal when the viewport
   * crosses the modal breakpoint while the drawer is open.
   */
  #onModalBreakpointChange = () => {
    if (!this.isOpen) return;

    const { panel } = this.refs;
    const nestedDialog = this.#getOpenNestedDialog();
    // Preserve wherever focus currently sits inside the drawer — not just inside
    // a nested dialog. A breakpoint cross can fire from a plain resize/rotation
    // while the user is typing in the discount input or cart note; closing and
    // reopening the dialog to switch modes would otherwise drop them onto the
    // close button (WCAG 2.4.3). The dialog element itself isn't re-rendered by
    // the mode switch, so the captured element is still connected afterwards.
    const activeBeforeSwitch =
      document.activeElement instanceof HTMLElement && panel.contains(document.activeElement)
        ? document.activeElement
        : null;
    const nestedActiveElement = nestedDialog?.contains(activeBeforeSwitch) ? activeBeforeSwitch : null;

    // Close the current dialog mode and immediately reopen in the new mode.
    // No animation — the drawer stays visually in place.
    panel.close();
    removeTrapFocus();

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      unlockScroll(panel);
      panel.show();
      trapFocus(panel);
    }

    if (nestedDialog?.open) {
      this.#bringNestedDialogToFront(nestedDialog, nestedActiveElement);
    } else if (activeBeforeSwitch?.isConnected) {
      activeBeforeSwitch.focus();
    } else {
      this.#focusInitialElement();
    }

    // Ensure keydown and click listeners are registered. The sessionStorage
    // restore path bypasses open(), so these may not have been added yet.
    // addEventListener deduplicates identical listeners, so this is safe
    // even if they were already registered.
    this.#addEventListeners();
  };

  /**
   * Registers the document and panel listeners that dismiss the drawer. The select listeners
   * are only attached when the drawer opts in with `data-close-on-select-press`.
   */
  #addEventListeners() {
    const { panel } = this.refs;

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);

    if (this.dataset.closeOnSelectPress === undefined) return;
    document.addEventListener('pointerdown', this.#onSelectInteraction, true);
    document.addEventListener('keydown', this.#onSelectInteraction, true);
  }

  /**
   * Closes a non-modal drawer when the shopper presses, or uses the keyboard to open, a native
   * `<select>` outside it.
   *
   * The browser draws a select's option list outside the page, above every z-index, so it
   * cannot be layered beneath the drawer and would paint over the panel. Dismissing the drawer
   * first avoids the clash. Focus is not restored, since the shopper has deliberately moved it
   * to the select.
   *
   * @param {PointerEvent | KeyboardEvent} event - The pointer or keyboard event.
   */
  #onSelectInteraction = (event) => {
    if (this.#modalQuery.matches) return;
    if (!(event.target instanceof Element)) return;
    if (event instanceof KeyboardEvent && !SELECT_OPEN_KEYS.includes(event.key)) return;

    const select = event.target.closest('select');
    if (!select) return;

    const isInsideDrawer = this.contains(select);
    const isInsideModal = select.closest('dialog:modal') !== null;
    if (isInsideDrawer || isInsideModal) return;

    this.close({ restoreFocus: false });
  };

  /**
   * Closes the drawer when the user presses Escape.
   * preventDefault() suppresses the native dialog cancel event in modal mode.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    // isComposing: don't close while an Input Method Editor (e.g. Japanese,
    // Chinese, Korean input) consumes Escape to dismiss its candidate list —
    // can happen inside the cart's discount-code field.
    // defaultPrevented: let nested components handle Escape first.
    if (event.isComposing || event.defaultPrevented) return;
    if (!this.isOpen) return;
    if (!this.#isTopmost()) return;

    event.preventDefault();
    this.close();
  };

  /**
   * Shows the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction. `'inline-start'` slides left-to-right (used when the chat docks).
   * @param {boolean} [options.focus] - Whether to move focus to the close button. Defaults to `true`; pass `false` for non-intentional opens (e.g. hover preview) that shouldn't steal keyboard focus.
   */
  open({ enter, focus = true } = {}) {
    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }
    if (this.#isClosing) {
      this.#deferredOpen = { enter, focus };
      return;
    }

    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);

    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      panel.show();
      trapFocus(panel);
    }

    if (focus) this.#focusInitialElement();

    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    this.setAttribute('open', '');
    this.dispatchEvent(new DrawerOpenEvent());

    this.#addEventListeners();
  }

  /**
   * Bumps this drawer's z-index to the top of the stack and replays
   * the slide-in animation. Called when open() is invoked on an
   * already-open drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction.
   */
  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    // In modal mode, dialogs live in the browser's top layer where z-index
    // is ignored — stacking follows showModal() call order. Re-calling
    // showModal() moves this dialog to the top of the stack.
    if (this.#modalQuery.matches && panel.open) {
      lockScroll(panel);
      panel.close();
      panel.showModal();
    }

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);
    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    // Re-fire so persistDrawerState picks up the new stack order.
    this.dispatchEvent(new DrawerOpenEvent());
  }

  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('pointerdown', this.#onSelectInteraction, true);
    document.removeEventListener('keydown', this.#onSelectInteraction, true);
    panel.removeEventListener('click', this.#onBackdropClick);
  }

  /**
   * Moves focus to the close button inside the drawer panel.
   * Every theme-drawer renders the close button via theme-drawer-header.
   * If a future drawer omits it, that drawer is responsible for its own focus management.
   */
  #focusInitialElement() {
    const closeButton = /** @type {HTMLElement | null} */ (
      this.refs.panel.querySelector('.theme-drawer__close-button')
    );
    closeButton?.focus();
  }

  /**
   * Reopens a nested modal after the drawer panel changes mode so it remains
   * above the drawer in the browser top layer.
   *
   * @param {HTMLDialogElement} dialog - The nested dialog to restack.
   * @param {HTMLElement | null} focusTarget - Element to refocus inside the nested dialog.
   */
  #bringNestedDialogToFront(dialog, focusTarget) {
    dialog.close();
    dialog.showModal();

    if (focusTarget && document.contains(focusTarget) && dialog.contains(focusTarget)) {
      focusTarget.focus();
    }
  }

  /**
   * Returns focus to the element that was focused before the drawer opened.
   *
   * A Section Rendering API morph between open and close can replace the trigger node. The JS
   * reference stays valid but the element is detached from the live DOM — and .focus() on a
   * detached node is a silent no-op, so we explicitly check and fall back to a fresh query.
   *
   * @param {HTMLElement | null} trigger - The element that was focused when the drawer opened.
   */
  #restoreFocusToTrigger(trigger) {
    if (trigger && document.contains(trigger)) {
      trigger.focus();
      return;
    }
    /** @type {HTMLElement | null} */ (document.querySelector(`[aria-controls="${this.id}"]`))?.focus();
  }

  /**
   * Slides the drawer out, waits for the animation, then closes the dialog.
   *
   * @param {object} [options]
   * @param {boolean} [options.restoreFocus] - Whether to return focus to the previously focused element.
   */
  async #closeDialog({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;
    if (this.#isClosing) return;
    this.#isClosing = true;

    clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = undefined;

    this.#removeEventListeners();
    removeTrapFocus();

    const { panel } = this.refs;

    this.removeAttribute('open');
    this.dispatchEvent(new DrawerCloseEvent());

    unlockScroll(panel);

    if (panel.open) {
      // Cancel any in-progress open animation before starting the close.
      panel.classList.remove('theme-drawer__dialog--opening', 'theme-drawer__dialog--opening-inline-start');

      panel.classList.add('theme-drawer__dialog--closing');
      await onAnimationEnd(panel, undefined, { subtree: false });
      panel.classList.remove('theme-drawer__dialog--closing');
    }

    panel.close();
    this.style.removeProperty('--drawer-stack-order');

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;
    if (restoreFocus) this.#restoreFocusToTrigger(trigger);

    this.#isClosing = false;

    // Reconcile: if open() was called during the close animation, reopen now.
    if (this.#deferredOpen) {
      const options = this.#deferredOpen;
      this.#deferredOpen = false;
      this.open(options);
    }
  }

  /**
   * Closes the drawer.
   *
   * @param {object} [options]
   * @param {boolean} [options.restoreFocus] - Whether to return focus to the previously focused element.
   *   Defaults to `true`; pass `false` when the user has deliberately moved focus elsewhere.
   */
  async close({ restoreFocus = true } = {}) {
    if (this.#isClosing) {
      // A close is already in progress — cancel any deferred open so the
      // drawer stays closed when the animation finishes.
      this.#deferredOpen = false;
      return;
    }
    await this.#closeDialog({ restoreFocus });
  }

  /**
   * Toggles the drawer. If the drawer is open but not the topmost in the
   * stack, it is brought to front instead of closed.
   */
  toggle() {
    if (this.isOpen && this.#isTopmost()) {
      return this.close();
    }
    return this.open();
  }

  /**
   * Whether hover/click on the trigger should be allowed to open the drawer
   * given its current content. Controlled by `data-open-when-empty` (set to
   * `"false"` to opt out) and, when opted out, whether the drawer's content
   * currently reports itself empty via `.cart-drawer--empty`. Defaults to
   * true so drawers that don't set the attribute keep opening unconditionally.
   */
  get #blockedByEmptyContent() {
    return this.dataset.openWhenEmpty === 'false' && Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * Opens the drawer on pointer hover — or, when already open, keeps it alive by
   * cancelling any pending idle-close. Also wired to `focus` (capture phase) so
   * that a keyboard or screen-reader user moving focus into the panel pauses the
   * auto-close timer, mirroring the hover behaviour (WCAG 2.2.1 / 1.4.13). No-op in modal mode
   * (narrow viewports have no hover affordance — the trigger's click handler opens
   * the drawer there instead), or when {@link #blockedByEmptyContent} opts the
   * drawer out of opening while empty.
   *
   * Bails out if already open rather than calling `open()` unconditionally:
   * the trigger's hit area and the panel's edge sit close enough that the
   * pointer can fire repeated `pointerenter`s crossing between them, and
   * `open()` on an already-open drawer replays the slide-in animation via
   * `#bringToFront()` — visible as a flicker if not guarded here.
   */
  openOnHover() {
    if (this.#modalQuery.matches) return;
    if (this.#blockedByEmptyContent) return;
    clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = undefined;
    if (this.isOpen) return;
    this.open({ focus: false });
  }

  /**
   * Schedules the drawer to close after the pointer leaves both the trigger
   * and the panel, or after keyboard focus leaves the panel (wired to `blur`,
   * capture phase). Re-entering — by pointer or focus — before the delay elapses
   * cancels this via {@link openOnHover}. When focus moves between elements
   * inside the panel, the `blur`/`focus` pair fires back-to-back so the
   * timer is cleared again before it can elapse. No-op in modal mode.
   */
  scheduleHoverClose() {
    if (this.#modalQuery.matches) return;
    // Drawers configured to stay open (data-auto-close="false") never schedule an idle
    // close — the drawer remains until the shopper dismisses it.
    if (this.dataset.autoClose === 'false') return;
    clearTimeout(this.#hoverCloseTimer);
    // Delay is configurable per drawer via data-hover-close-delay (ms); falls back to the default.
    const delay = Number(this.dataset.hoverCloseDelay) || HOVER_CLOSE_DELAY_MS;
    this.#hoverCloseTimer = setTimeout(() => {
      this.#hoverCloseTimer = undefined;
      this.close();
    }, delay);
  }

  /**
   * Handles a click on a hover-enabled trigger anchor.
   *
   * In modal mode (narrow viewports, no hover affordance) the anchor always opens
   * the drawer instead of navigating.
   *
   * On wide viewports the pointer opens the drawer on hover, so a genuine mouse
   * click is left to navigate to the full cart page. A keyboard activation
   * (Enter/Space) can't hover, though, and would otherwise be the only input that
   * never reaches the drawer — so it gets the same drawer the pointer does.
   * Keyboard-generated clicks carry no pointer coordinates and report
   * `event.detail === 0`; real mouse clicks report `>= 1`.
   *
   * In every case the anchor is left to navigate normally when
   * {@link #blockedByEmptyContent} opts the drawer out of opening while empty and
   * it isn't already open.
   *
   * @param {MouseEvent} event - The click event.
   */
  handleHoverTriggerClick(event) {
    const keyboardActivated = event.detail === 0;
    if (!this.#modalQuery.matches && !keyboardActivated) return;
    if (this.#blockedByEmptyContent && !this.isOpen) return;
    event.preventDefault();
    this.toggle();
  }

  /**
   * @returns {boolean} Whether this drawer has the highest stack order among all open drawers.
   */
  #isTopmost() {
    const myOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    for (const other of document.querySelectorAll('theme-drawer[open]')) {
      if (other === this) continue;
      const otherOrder = Number(/** @type {HTMLElement} */ (other).style.getPropertyValue('--drawer-stack-order') || 0);
      if (otherOrder > myOrder) return false;
    }
    return true;
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:close';
}
